import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

type Tab = "terms" | "privacy" | "disclaimers";

const tabs: { id: Tab; label: string }[] = [
  { id: "terms", label: "Terms of Service" },
  { id: "privacy", label: "Privacy Policy" },
  { id: "disclaimers", label: "Disclaimers" },
];

export default function Legal() {
  const location = useLocation();
  const hashTab = location.hash.replace("#", "") as Tab;
  const [activeTab, setActiveTab] = useState<Tab>(
    tabs.some((t) => t.id === hashTab) ? hashTab : "terms"
  );

  useEffect(() => {
    const h = location.hash.replace("#", "") as Tab;
    if (tabs.some((t) => t.id === h)) {
      setActiveTab(h);
    }
  }, [location.hash]);

  return (
    <>
      <Helmet>
        <title>Legal | UKPropertyChecks</title>
        <meta
          name="description"
          content="Terms of Service, Privacy Policy, and Disclaimers for UKPropertyChecks."
        />
        <link rel="canonical" href="https://ukpropertychecks.co.uk/legal" />
      </Helmet>

      <main className="bg-white">
        <div className="container-main section-spacing">
          <h1>Legal</h1>
          <p className="mt-3 text-base leading-7 text-slate-800">
            Information and document preparation only. Not legal advice. Does
            not replace a solicitor or planning professional.
          </p>

          <nav aria-label="Legal sections" className="mt-8 border-b border-slate-200">
            <ul className="flex gap-0" role="tablist">
              {tabs.map((tab) => (
                <li key={tab.id} role="presentation">
                  <a
                    href={`#${tab.id}`}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    aria-controls={`panel-${tab.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab(tab.id);
                      window.history.replaceState(null, "", `#${tab.id}`);
                    }}
                    className={`inline-block px-4 py-3 text-sm font-medium min-h-[44px] leading-[44px] border-b-2 transition-colors focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[#ffdd00] focus-visible:outline-offset-2 ${
                      activeTab === tab.id
                        ? "border-emerald-700 text-emerald-700"
                        : "border-transparent text-slate-600 hover:text-slate-950 hover:border-slate-300"
                    }`}
                  >
                    {tab.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="mt-8">
            {activeTab === "terms" && <TermsContent />}
            {activeTab === "privacy" && <PrivacyContent />}
            {activeTab === "disclaimers" && <DisclaimersContent />}
          </div>
        </div>
      </main>
    </>
  );
}

function TermsContent() {
  return (
    <article
      id="panel-terms"
      role="tabpanel"
      aria-labelledby="tab-terms"
      className="prose-legal"
    >
      <p className="text-sm text-slate-600 mb-6">
        Last updated: 1 March 2026. Effective: 1 March 2026.
      </p>

      <Section title="1. Acceptance of Terms">
        <p>
          By accessing or using UKPropertyChecks ("Service"), you agree to be
          bound by these Terms of Service. If you do not agree, do not use the
          Service.
        </p>
      </Section>

      <Section title="2. Description of Service">
        <p>UKPropertyChecks provides:</p>
        <ul>
          <li>Buyer's Report — pre-offer due-diligence summary of public records for a UK property (planning, EPC, flood risk, heritage, alterations, tax/regulatory rules)</li>
          <li>Seller's Pack — solicitor-ready PDF of TA6, TA7, and LPE1 conveyancing forms, prepared from a guided questionnaire and uploaded documents</li>
          <li>Document processing to extract field values from uploaded documents</li>
        </ul>
        <p>
          This service does not provide legal advice. It prepares information
          and documents only.
        </p>
      </Section>

      <Section title="3. Accounts and Anonymous Purchases">
        <p>
          You can buy a Buyer's Report without creating an account. We offer to
          save your purchase to an account after payment so you can return to
          it later, but that step is optional. The Seller's Pack flow requires
          an account so you can save progress on the disclosure questionnaire.
        </p>
        <p>
          When you do create an account, you must provide accurate information
          and you are responsible for maintaining the security of the email
          account used for authentication. You must be at least 18 years old.
          Notify us immediately of any unauthorised access.
        </p>
      </Section>

      <Section title="4. Pricing and Payment">
        <ul>
          <li>Buyer's Report: £29 per report</li>
          <li>Seller's Pack: £79 per pack</li>
        </ul>
        <p>
          All payments are processed via Stripe in GBP (£). We do not store
          your payment card details. Payments are non-refundable except as
          required by law or described in Section 11.
        </p>
      </Section>

      <Section title="5. Permitted and Prohibited Use">
        <p>
          You may use the Service for personal property research, transaction
          preparation, and due diligence. You may not resell or redistribute
          reports, use the Service for illegal purposes, reverse engineer or
          scrape the Service, or submit false information.
        </p>
      </Section>

      <Section title="6. Intellectual Property">
        <p>
          You retain ownership of documents you upload. You grant us a licence
          to process your documents to provide the Service. Generated reports
          are licensed for use in your property transaction only and may not be
          resold.
        </p>
      </Section>

      <Section title="7. Third-Party Data">
        <p>
          The Service uses data from Ideal Postcodes, Historic England (National
          Heritage List), Planning Data Service, local authority planning
          systems, the Energy Performance of Buildings Register (Open Data
          Communities, Open Government Licence), and The Law Society form
          templates. We do not guarantee the accuracy of third-party data and
          you should verify findings independently before acting on them.
        </p>
      </Section>

      <Section title="8. AI-Generated Content">
        <p>
          The Service uses AI to process documents and generate content.
          AI-generated content may contain errors. All generated forms must be
          reviewed by a qualified solicitor before use. We are not responsible
          for errors in AI-generated content.
        </p>
      </Section>

      <Section title="9. Disclaimers and Limitations">
        <p>
          The Service is provided "as is" without warranties. We do not
          guarantee uninterrupted or error-free service. Our total liability is
          limited to the amount you paid. The Service does not provide legal,
          financial, or professional advice.
        </p>
      </Section>

      <Section title="10. Data Protection">
        <p>
          We process your personal data in accordance with our Privacy Policy
          and comply with UK GDPR. Your data is stored securely in the UK.
          Uploaded source documents are deleted after 90 days; generated
          Buyer's Reports and Seller's Packs are retained in your account for
          12 months. You can request earlier deletion at any time.
        </p>
      </Section>

      <Section title="11. Refunds">
        <p>
          Reports and packs are digital goods delivered immediately. Refunds
          are generally not available after delivery but may be provided for
          technical errors, duplicate purchases, or service failures. Contact
          us within 14 days to dispute a charge.
        </p>
      </Section>

      <Section title="12. Changes to Terms">
        <p>
          We may modify these Terms at any time. We will notify you of material
          changes via email. Continued use after changes constitutes
          acceptance. You have 30 days to object, after which you may terminate
          your account.
        </p>
      </Section>

      <Section title="13. Termination">
        <p>
          You may terminate your account at any time. We may terminate accounts
          for Terms violations. Download your reports before termination.
        </p>
      </Section>

      <Section title="14. Governing Law">
        <p>
          These Terms are governed by the laws of England and Wales. Nothing in
          these Terms affects your statutory consumer rights under the Consumer
          Rights Act 2015.
        </p>
      </Section>

      <Section title="15. Contact">
        <p>
          For questions about these Terms, email{" "}
          <a href="mailto:legal@ukpropertychecks.co.uk" className="text-blue-700 underline">
            legal@ukpropertychecks.co.uk
          </a>
          .
        </p>
        <p className="text-sm text-slate-600 mt-2">
          Company registration: pending registration.
        </p>
      </Section>
    </article>
  );
}

function PrivacyContent() {
  return (
    <article
      id="panel-privacy"
      role="tabpanel"
      aria-labelledby="tab-privacy"
      className="prose-legal"
    >
      <p className="text-sm text-slate-600 mb-6">
        Last updated: 1 March 2026. Effective: 1 March 2026.
      </p>

      <Section title="1. Introduction">
        <p>
          UKPropertyChecks is committed to protecting your privacy. This policy
          explains how we collect, use, store, and protect your personal data.
          It complies with the UK General Data Protection Regulation (UK GDPR)
          and the Data Protection Act 2018.
        </p>
      </Section>

      <Section title="2. Data Controller">
        <p>
          UKPropertyChecks Ltd. ICO registration: pending registration. Email:{" "}
          <a href="mailto:privacy@ukpropertychecks.co.uk" className="text-blue-700 underline">
            privacy@ukpropertychecks.co.uk
          </a>
        </p>
      </Section>

      <Section title="3. What Data We Collect">
        <ul>
          <li>Email address (for authentication and communications)</li>
          <li>Property addresses you search or analyse</li>
          <li>Documents you upload (leases, title deeds, etc.)</li>
          <li>Payment information (processed by Stripe — we do not store card details)</li>
          <li>Usage data (access logs, features used, error logs)</li>
          <li>Essential cookies for authentication and session management</li>
        </ul>
      </Section>

      <Section title="4. How We Use Your Data">
        <ul>
          <li>To authenticate your account and manage sessions</li>
          <li>To generate Buyer's Reports and Seller's Packs</li>
          <li>To process payments</li>
          <li>To send transactional emails (verification codes, receipts)</li>
          <li>To improve the service and fix technical issues</li>
          <li>To prevent fraud and comply with legal obligations</li>
        </ul>
      </Section>

      <Section title="5. Data Sharing">
        <p>We share data with:</p>
        <ul>
          <li>Stripe — payment processing (EU/UK)</li>
          <li>AWS — cloud infrastructure (London region)</li>
          <li>Resend — transactional email delivery (EU)</li>
          <li>Ideal Postcodes — address validation (UK)</li>
          <li>Historic England — heritage data (UK)</li>
          <li>Department for Levelling Up, Housing and Communities — Energy Performance of Buildings Register (UK)</li>
        </ul>
        <p>
          We do not sell your personal data. We do not share data for
          advertising purposes.
        </p>
      </Section>

      <Section title="6. Data Storage and Security">
        <p>
          All data is stored in AWS data centres in the UK (London region).
          Documents are encrypted at rest (AES-256) and in transit (TLS 1.2+).
          No data is transferred outside the UK/EU.
        </p>
      </Section>

      <Section title="7. Data Retention">
        <ul>
          <li>Account data: until you delete your account</li>
          <li>Uploaded source documents: 90 days, then automatically deleted</li>
          <li>Generated Buyer's Reports and Seller's Packs: 12 months, then archived</li>
          <li>Payment records: 7 years (tax and accounting)</li>
          <li>Access logs: 90 days</li>
        </ul>
        <p>You can request earlier deletion at any time.</p>
      </Section>

      <Section title="8. Your Rights">
        <p>Under UK GDPR, you have the right to:</p>
        <ul>
          <li>Access your personal data</li>
          <li>Correct inaccurate data</li>
          <li>Request deletion of your data</li>
          <li>Restrict processing</li>
          <li>Data portability</li>
          <li>Object to processing</li>
          <li>Request human review of AI decisions</li>
          <li>Withdraw consent at any time</li>
        </ul>
        <p>
          To exercise your rights, email{" "}
          <a href="mailto:privacy@ukpropertychecks.co.uk" className="text-blue-700 underline">
            privacy@ukpropertychecks.co.uk
          </a>
          . We will respond within 30 days.
        </p>
      </Section>

      <Section title="9. Cookies">
        <p>
          We use essential cookies for authentication and session management.
          These cannot be disabled as they are required for the service to
          function. We do not use advertising or tracking cookies.
        </p>
      </Section>

      <Section title="10. Complaints">
        <p>
          If you are not satisfied with how we handle your data, you can
          complain to the Information Commissioner's Office (ICO) at{" "}
          <a
            href="https://ico.org.uk/make-a-complaint/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-700 underline"
          >
            ico.org.uk/make-a-complaint
          </a>{" "}
          or call 0303 123 1113.
        </p>
      </Section>
    </article>
  );
}

function DisclaimersContent() {
  return (
    <article
      id="panel-disclaimers"
      role="tabpanel"
      aria-labelledby="tab-disclaimers"
      className="prose-legal"
    >
      <Section title="Buyer's Report">
        <p>
          The Buyer's Report is for informational purposes only and does not
          constitute planning, legal, or financial advice. The report is based
          on publicly available data which may be incomplete, outdated, or
          inaccurate. Planning regulations, local authority policies, and tax
          rules change frequently. Title summary is omitted entirely until our
          HM Land Registry integration is live.
        </p>
        <p>
          Verify findings with the relevant local planning authority, your
          solicitor, and a qualified planning consultant before acting on
          anything in the report. We do not guarantee the accuracy,
          completeness, or timeliness of the information.
        </p>
      </Section>

      <Section title="Seller's Pack">
        <p>
          The Seller's Pack contains draft answers to the official TA6, TA7,
          and LPE1 forms. Some answers are extracted from your uploaded
          documents using OCR and AI and may contain errors. The pack must be
          reviewed and submitted by a qualified solicitor before completion.
          Do not submit generated forms without professional legal review.
        </p>
        <p>
          This service does not provide legal advice. We are not a law firm or
          legal service provider. The pack is based only on the questionnaire
          answers you give and the documents you upload — missing information
          will result in incomplete forms.
        </p>
      </Section>

      <Section title="AI-Generated Content">
        <p>
          This service uses AI to process documents and extract information. AI
          may misinterpret or incorrectly extract information. Confidence
          scores indicate AI certainty but do not guarantee accuracy.
          Low-confidence extractions are flagged but may still contain errors.
        </p>
        <p>
          All AI-generated content should be verified against source documents.
          AI does not replace professional judgement.
        </p>
      </Section>

      <Section title="Third-Party Data">
        <p>
          The service uses data from Ideal Postcodes, Historic England
          (National Heritage List), Planning Data Service, the Energy
          Performance of Buildings Register (Open Data Communities, Open
          Government Licence), and local authority planning systems.
          Third-party data may be incomplete, outdated, or inaccurate. Data
          coverage varies by location. Verify findings with official sources
          before acting on them.
        </p>
      </Section>

      <Section title="Service Availability">
        <p>
          This service is provided "as is" without warranties of any kind. We
          do not guarantee uninterrupted or error-free operation. We may
          suspend or terminate the service at any time for maintenance or
          updates.
        </p>
      </Section>
    </article>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-semibold text-slate-950 mb-3">{title}</h2>
      <div className="space-y-3 text-base leading-7 text-slate-800 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:text-slate-800">
        {children}
      </div>
    </section>
  );
}
